import { useEffect, useRef, useCallback } from 'react';
import { condoApi } from '../services/api';
import { logger } from '../services/logger';
import type { Visitor, Event } from '../components/OperationalView/types';

// How far back to look in access logs when recovering after a reconnect
const LOOKBACK_HOURS = 4;

/**
 * Reconciles visitors stuck in 'awaiting' state against actual access logs.
 *
 * Root cause: iot-connect has two independent output pipelines:
 *   1. iot-connect → Redis → SSE → frontend  (event panel shows entry ✓)
 *   2. iot-connect → SQS → event-processor → DB state update  (sometimes fails ✗)
 * When the SQS path fails, the visitor stays 'awaiting' even though an entry event
 * was recorded in the access log.
 *
 * Two passes — designed to add ZERO load in steady state:
 *
 *   Pass A (API) — only fires on mount and on each WebSocket reconnection.
 *     Queries access-logs from the DB to catch events missed during deployment gaps,
 *     SSE drops, or before the app was open. Rate-limited so it never runs twice
 *     within 15 seconds for the same customer.
 *
 *   Pass B (live) — runs in-memory on every new SSE event.
 *     No API call — just cross-references the already-loaded event buffer.
 *     Zero backend load.
 */
export function useAwaitingEntryGuard(
  customerId: string | null,
  allVisitors: Visitor[],
  recentEvents: Event[],
  refreshVisitors: () => void,
  /** Pass visitorWsStatus from useVisitorWebSocket to trigger recovery on reconnect */
  wsConnectionStatus?: string
): void {
  const processedRoleIds = useRef<Set<string>>(new Set());
  const lastApiFetchRef = useRef<Map<string, number>>(new Map());
  const prevWsStatusRef = useRef<string | undefined>(undefined);

  // Reset on customer change
  useEffect(() => {
    processedRoleIds.current.clear();
  }, [customerId]);

  // ── Shared: given entry events, auto check-in matched awaiting visitors ──────
  const reconcile = useCallback(async (
    cid: string,
    awaitingVisitors: Visitor[],
    entryEvents: Array<{ personId: string; timestamp: string }>
  ) => {
    if (awaitingVisitors.length === 0 || entryEvents.length === 0) return;

    const latestByPerson = new Map<string, string>();
    for (const e of entryEvents) {
      const existing = latestByPerson.get(e.personId);
      if (!existing || e.timestamp > existing) latestByPerson.set(e.personId, e.timestamp);
    }

    for (const visitor of awaitingVisitors) {
      const { personId, roleId } = visitor;
      if (!personId || !roleId) continue;
      if (processedRoleIds.current.has(roleId)) continue;

      const entryTs = latestByPerson.get(personId);
      if (!entryTs) continue;

      const roleCreatedAt = (visitor as any).role_created_at as string | undefined;
      if (roleCreatedAt && entryTs < roleCreatedAt) continue;

      processedRoleIds.current.add(roleId);

      logger.info('useAwaitingEntryGuard', 'Auto check-in: visitor stuck in awaiting despite entry event', {
        personId,
        roleId,
        visitorName: visitor.name,
        entryTs,
        roleCreatedAt,
      });

      try {
        await condoApi.autoCheckIn(cid, personId, roleId);
        refreshVisitors();
      } catch (err) {
        processedRoleIds.current.delete(roleId);
        logger.warn('useAwaitingEntryGuard', 'autoCheckIn failed, will retry on next reconnect', { personId, roleId, err });
      }
    }
  }, [refreshVisitors]);

  // ── Pass A: API fetch — only on mount or reconnect ──────────────────────────
  const runApiPass = useCallback(async (cid: string, visitors: Visitor[]) => {
    const awaitingVisitors = visitors.filter(
      v => v.status === 'awaiting' && v.personId && v.roleId
    );
    // Nothing to do — bail immediately, zero API calls
    if (awaitingVisitors.length === 0) return;

    // Rate-limit: don't fire twice within 15s for the same customer
    const now = Date.now();
    const last = lastApiFetchRef.current.get(cid) ?? 0;
    if (now - last < 15_000) return;
    lastApiFetchRef.current.set(cid, now);

    logger.info('useAwaitingEntryGuard', 'API pass: checking access logs for stuck awaiting visitors', {
      cid, awaitingCount: awaitingVisitors.length,
    });

    try {
      const since = new Date(Date.now() - LOOKBACK_HOURS * 60 * 60 * 1000).toISOString();
      const response = await condoApi.getAccessLogs({
        customer_id: cid,
        limit: 200,
        order: 'desc',
        since,
      });

      const logs: any[] = response.data?.data || response.data || [];
      const entryEvents = logs
        .filter((log: any) =>
          log.person_id &&
          (log.direction === 'entrada' ||
           log.event_type?.includes('entry') ||
           log.event_type?.includes('check_in'))
        )
        .map((log: any) => ({
          personId: log.person_id as string,
          timestamp: (log.processed_at || log.event_timestamp || log.created_at) as string,
        }));

      await reconcile(cid, awaitingVisitors, entryEvents);
    } catch (err) {
      logger.warn('useAwaitingEntryGuard', 'API pass failed', { cid, err });
    }
  }, [reconcile]);

  // ── Pass B: live SSE — runs in-memory, zero API calls ───────────────────────
  const runLivePass = useCallback(async (cid: string, visitors: Visitor[], events: Event[]) => {
    const awaitingVisitors = visitors.filter(
      v => v.status === 'awaiting' && v.personId && v.roleId
    );
    if (awaitingVisitors.length === 0) return;

    const entryEvents = events
      .filter(e => e.type === 'entry' && e.personId && e.status === 'success')
      .map(e => ({ personId: e.personId!, timestamp: e.timestamp || e.time }));

    await reconcile(cid, awaitingVisitors, entryEvents);
  }, [reconcile]);

  // ── Trigger A on mount (deployment / fresh open) ────────────────────────────
  // Small delay lets the WebSocket visitor snapshot settle first.
  useEffect(() => {
    if (!customerId) return;
    const timer = setTimeout(() => runApiPass(customerId, allVisitors), 2_000);
    return () => clearTimeout(timer);
    // Only runs when customerId changes (mount / tab switch), NOT on every visitor update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // ── Trigger A on WebSocket reconnection ─────────────────────────────────────
  // Fires once each time the WS transitions back to 'connected' — catches the case
  // where the WS dropped mid-session and events were missed during the gap.
  useEffect(() => {
    const prev = prevWsStatusRef.current;
    prevWsStatusRef.current = wsConnectionStatus;

    // Only act on the reconnection transition (non-connected → connected)
    if (wsConnectionStatus !== 'connected') return;
    if (prev === 'connected' || prev === undefined) return; // first render or already connected
    if (!customerId) return;

    logger.info('useAwaitingEntryGuard', 'WS reconnected — running API recovery pass', { customerId });
    // Short delay so the fresh visitor snapshot arrives before we reconcile
    const timer = setTimeout(() => runApiPass(customerId, allVisitors), 1_500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnectionStatus, customerId]);

  // ── Trigger B on every new live event (in-memory, no API) ───────────────────
  useEffect(() => {
    if (!customerId) return;
    runLivePass(customerId, allVisitors, recentEvents);
    // allVisitors intentionally omitted: we only want to react to new events,
    // not re-run every time the visitor list refreshes after a successful check-in
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, recentEvents]);
}
